import React, {
    Children,
    cloneElement,
    PropsWithChildren,
    ReactElement,
    useEffect,
    useState
} from "react";

import { css, keyframes } from "@compiled/react";
import { useIntl } from "react-intl";

import ChevronDownIcon from "@atlaskit/icon/core/chevron-down";
import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";

import { Initializer } from "../../../../analytics/atl-analytics/initializer";
import {
    LogoV3 as Logo,
    LogoBrandV3 as LogoBrand,
    ProductKeyTypes
} from "../../../../libraries/wpl/foundations/src/index";
import { isEqualUnlocalizedPathname, useLocation } from "../../../../utils";
import Button, { ButtonType } from "../../button/src";

export interface ProductNavBarProps {
    productHref: string; // link for logo
    productName: LogoBrand;
    showTextWithoutLogo?: boolean;
    showFreeHrefButton?: boolean;
    showContactSalesHrefButton?: boolean;
    freeHref?: string; // link for Get it free button
    ariaLabel?: string;
    dataProductKey: ProductKeyTypes; // product key label for Get it free button
    logoLinkWrapperWidth?: string;
    onClick?: (e: React.MouseEvent) => void;
}

const navigationWrapperCss = css({
    position: "fixed",
    width: "100%",
    insetBlockStart: 0,
    zIndex: 3
});

const navigationBarWrapperCss = css({
    alignItems: "flex-start",
    background: wacToken("color.neutral.light0"),
    display: "flex",
    flexDirection: "row",
    justifyContent: "center"
});

const navigationBarCss = css({
    alignItems: "center",
    display: "flex",
    flex: 1,
    justifyContent: "space-between",
    [media.above.md]: {
        height: "60px",
        maxWidth: "1200px",
        padding: `0 ${token("space.200")}`
    },
    [media.only.sm]: {
        height: "56px",
        padding: `0 5px 0 ${token("space.200")}`,
        position: "relative",
        zIndex: 1
    },
    [media.below.sm]: {
        flexFlow: "row wrap",
        height: "auto",
        padding: `0 ${token("space.200")}`
    }
});

const logoAndLinksCss = css({
    display: "flex"
});

const keyframeChevron = keyframes({
    "0%": {
        transform: "rotate(0deg)"
    },
    "100%": {
        transform: "rotate(-180deg)"
    }
});

const removeKeyframeChevron = keyframes({
    "0%": {
        transform: "rotate(-180deg)"
    },
    "100%": {
        transform: "rotate(0deg)"
    }
});

const dropdownSlideInAnimation = keyframes({
    from: {
        maxHeight: "0px"
    },
    to: {
        maxHeight: "500px"
    }
});

const dropdownSlideOutAnimation = keyframes({
    from: {
        maxHeight: "500px"
    },
    to: {
        maxHeight: "0px",
        display: "none"
    }
});

const listItemCss = css({
    position: "relative"
});

const buttonWrapper = css({
    display: "flex",
    padding: "15px 0"
});

const linkAndButtonWrapper = css({
    display: "flex",
    alignItems: "center"
});

const buttonStyle = css({
    fontSize: "1rem",
    fontWeight: 500,
    height: "2rem",
    width: "auto",
    float: "right",
    transition: "background 250ms",
    textWrap: "nowrap",
    [media.below.sm]: {
        position: "relative",
        width: "auto",
        flexFlow: "row wrap",
        insetInlineEnd: "unset"
    }
});

const contactSalesButtonStyle = css({
    fontSize: "1rem",
    fontWeight: 500,
    height: "2rem",
    background: `${wacToken("color.extended.saffron400")} !important`,
    width: "auto",
    float: "right",
    transition: "background 250ms",
    textWrap: "nowrap",
    [media.below.sm]: {
        position: "relative",
        width: "auto",
        flexFlow: "row wrap",
        insetInlineEnd: "unset"
    }
});

const logoDimensions: Partial<Record<LogoBrand, number>> = {
    confluence: 157,
    jiraservicemanagement: 280,
    jira: 80,
    jirasoftware: 80
};

// eslint-disable-next-line complexity
export const ProductNavBar = (props: PropsWithChildren<ProductNavBarProps>) => {
    const intl = useIntl();
    const isShowFreeHrefButton = props.showFreeHrefButton ?? true;
    const isShowContactSalesHrefButton =
        props.showContactSalesHrefButton ?? false;
    const showTextWithoutLogo = props.showTextWithoutLogo ?? false;

    const [activeDropdown, setActive] = useState<boolean | null>(null);

    const [buttonType, setButtonType] = useState<ButtonType>("secondary");

    const location = useLocation();

    useEffect(() => {
        window.addEventListener("scroll", handleScroll);
    }, []);

    useEffect(() => {
        Initializer.autoTrack();
    }, [activeDropdown, props.showFreeHrefButton]);

    const handleScroll = () => {
        setButtonType(window.scrollY > 56 ? "primary" : "secondary");
    };

    const getItFreeText = intl.formatMessage({
        id: "productNavBar.get-it-free-button",
        defaultMessage: "Get it free"
    });

    const getContactSalesText = intl.formatMessage({
        id: "productNavBar.contact-sales-button",
        defaultMessage: "Contact sales"
    });

    const dropdownCss = [
        css({
            display: "none",
            cursor: "pointer",
            backgroundColor: "transparent",
            border: "none",
            color: wacToken("color.extended.blue800"),
            paddingBlockStart: token("space.075"),
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            svg: {
                height: token("space.400"),
                width: token("space.400"),
                animationFillMode: "forwards"
            },
            [media.below.md]: {
                display: "block"
            }
        }),
        !activeDropdown &&
            css({
                // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                svg: {
                    animation: `${removeKeyframeChevron} 300ms ease`
                }
            }),
        activeDropdown &&
            css({
                // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                svg: {
                    animation: `${keyframeChevron} 300ms ease`
                }
            })
    ];

    const mobileCategoriesWrapperCss = [
        css({
            flex: 1,
            boxShadow: `0 2px 10px ${"rgba(0, 0, 0, .25)"}`,
            maxHeight: "0px",
            overflowX: "hidden",
            overflowY: "auto",
            position: "absolute",
            width: "100%",
            backgroundColor: wacToken("color.neutral.light100"),
            borderBlockEnd: `1px solid ${wacToken("color.neutral.light300")}`,
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            ul: {
                margin: 0,
                padding: "15px 0px 15px 30px",
                listStyleType: "none",
                [media.below.md]: {
                    paddingInlineEnd: "15px"
                }
            },
            [media.above.md]: {
                display: "none"
            }
        }),
        activeDropdown === null &&
            css({
                borderColor: wacToken("color.neutral.light300"),
                borderBlockStart: `1px solid ${wacToken(
                    "color.neutral.light300"
                )}`
            }),
        activeDropdown &&
            css({
                maxHeight: "1000px",
                animation: `${dropdownSlideInAnimation} 800ms ease forwards`,
                borderColor: wacToken("color.extended.blue1000"),
                borderBlockStart: `1px solid ${wacToken(
                    "color.neutral.light400"
                )}`
            }),
        !activeDropdown &&
            css({
                animation: `${dropdownSlideOutAnimation} 600ms ease forwards`,
                borderColor: wacToken("color.neutral.light300"),
                borderBlockStart: `1px solid ${wacToken(
                    "color.neutral.light400"
                )}`
            })
    ];

    const renderSelectionItems = (isMobile: boolean) => {
        const categoriesWrapperCss = [
            !isMobile &&
                css({
                    display: "flex",
                    flex: 1,
                    flexDirection: "row",
                    listStyle: "none",
                    margin: `0 ${token("space.200")}`,
                    padding: 0,
                    [media.below.md]: {
                        display: "none"
                    }
                })
        ];
        return (
            <ul css={categoriesWrapperCss}>
                {props.children &&
                    Children.map(props.children, (child, index) =>
                        child ? (
                            <li key={index} css={listItemCss}>
                                {cloneElement(child as ReactElement, {
                                    isSelected:
                                        (child as ReactElement).props
                                            .isSelected ||
                                        isEqualUnlocalizedPathname(
                                            (child as ReactElement).props.href,
                                            location.pathname
                                        ),
                                    isMobile
                                })}
                            </li>
                        ) : null
                    )}
            </ul>
        );
    };

    const renderDropdownButton = () => {
        if (props.children) {
            return (
                <button
                    css={dropdownCss}
                    onClick={() => setActive(!activeDropdown)}
                >
                    <ChevronDownIcon label="" />
                </button>
            );
        }
        return null;
    };

    const logoWrapperCss = [
        props.logoLinkWrapperWidth &&
            props.logoLinkWrapperWidth === "" &&
            css({
                width: "157px"
            }),
        css({
            alignItems: "center",
            display: "flex"
        })
    ];

    const textWrapperCss = css({
        height: "50px",
        color: wacToken("color.extended.blue1000"),
        alignItems: "center",
        display: "flex",
        flexDirection: "row"
    });

    const textCss = css({
        fontSize: "1.4rem",
        fontWeight: 500
    });

    const boxShadowCss =
        props.productName !== "confluence" &&
        props.productName !== "jira" &&
        css({
            boxShadow: `0 2px 10px ${"rgba(0, 0, 0, 0.25)"}`
        });

    return (
        <div css={navigationWrapperCss}>
            <div css={[navigationBarWrapperCss, boxShadowCss]}>
                <div css={navigationBarCss} className="productNavBar">
                    <div css={logoAndLinksCss}>
                        {showTextWithoutLogo ? (
                            <a
                                css={textWrapperCss}
                                href={props.productHref}
                                aria-label={props.ariaLabel}
                            >
                                <span css={textCss}>{props.ariaLabel}</span>
                            </a>
                        ) : (
                            <a
                                css={logoWrapperCss}
                                href={props.productHref}
                                aria-label={props.ariaLabel}
                            >
                                <Logo
                                    brand={props.productName}
                                    type="logo"
                                    appearance="brand"
                                    height={"48"}
                                    width={logoDimensions[props.productName]}
                                />
                            </a>
                        )}
                        {renderDropdownButton()}
                        {renderSelectionItems(false)}
                    </div>
                    <div css={linkAndButtonWrapper}>
                        {isShowFreeHrefButton && (
                            <div
                                css={buttonWrapper}
                                className="showFreeHrefButton"
                            >
                                <Button
                                    text={getItFreeText}
                                    css={buttonStyle}
                                    url={props.freeHref}
                                    type={buttonType}
                                    aria-label={getItFreeText}
                                    dataEventProps={{
                                        "data-event-container": "navbar",
                                        "data-event": "clicked",
                                        "data-event-component": "linkButton",
                                        "data-label": getItFreeText,
                                        "data-label-english": "Get it free",
                                        "data-product-key":
                                            props.dataProductKey,
                                        "data-track-event": `${props.dataProductKey}.get-it-free-button-clicked`
                                    }}
                                    onClick={props.onClick}
                                />
                            </div>
                        )}
                        {isShowContactSalesHrefButton && (
                            <div className="showContactSalesHrefButton">
                                <Button
                                    text={getContactSalesText}
                                    css={contactSalesButtonStyle}
                                    color="yellow"
                                    url={props.freeHref}
                                    type={buttonType}
                                    aria-label={getContactSalesText}
                                    dataEventProps={{
                                        "data-event-container": "navbar",
                                        "data-event": "clicked",
                                        "data-event-component": "linkButton",
                                        "data-label": getContactSalesText,
                                        "data-product-key": props.dataProductKey
                                    }}
                                    onClick={props.onClick}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div
                css={mobileCategoriesWrapperCss}
                className="mobileCategoriesWrapper"
            >
                {renderSelectionItems(true)}
            </div>
        </div>
    );
};

export default ProductNavBar;
